import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import dayjs from 'dayjs';
import localizedTime from 'dayjs/plugin/localizedFormat';
import { initReactI18next } from 'react-i18next';

import { CatalogueModule, bindI18n, createGlobBackend } from '~common/i18n';
import { LanguageCode, normalizeLanguageCode, supportedLanguages } from '~common/languages';

dayjs.extend(localizedTime);

/**
 * updates the HTML lang attribute for accessibility compliance
 *
 * @param language - the language code to set
 */
const updateHtmlLangAttribute = (language: string) => {
  try {
    document.documentElement.lang = language;
  } catch (error) {
    console.warn('Failed to update HTML lang attribute:', error);
  }
};

/**
 * Find all available translation files for play-ui and the app client,
 * they will be transformed to 18next json by our vite-plugin-po-to-json
 */
const playUiTranslations = import.meta.glob<CatalogueModule>('~play-ui/translations/*.po');
const appTranslations = import.meta.glob<CatalogueModule>('../translations/*.po');

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(createGlobBackend({ app: appTranslations, 'play-ui': playUiTranslations }))
  .init({
    detection: {
      convertDetectedLanguage: normalizeLanguageCode,
    },
    supportedLngs: supportedLanguages,
    fallbackLng: ({
      fr: ['fr-CH'] as LanguageCode[],
      it: ['it-CH'] as LanguageCode[],
      default: ['en'] as LanguageCode[],
    }),
    keySeparator: false,
    nsSeparator: false,

    ns: ['app', 'play-ui'],
    defaultNS: 'app',
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  }, (err) => {
    dayjs.locale(i18n.language);

    // set initial HTML lang attribute
    updateHtmlLangAttribute(i18n.language);

    if (err) {
      console.error('error while initializing i18next: ' + JSON.stringify(err));
    }
  });

i18n.on('languageChanged', lng => {
  console.info(`Changed language to "${lng}"`);
  dayjs.locale(lng);
  updateHtmlLangAttribute(lng);
});

bindI18n(i18n);

export default i18n;
